import React, { useCallback, useEffect, useState } from 'react';
import { getWalletBalance, secureFetch } from '../../supabaseService';
import WalletRechargePanel from '../wallet/WalletRechargePanel';

const REASON_CODES = [
  { id: 'HOURS_MISMATCH', label: 'Hours mismatch' },
  { id: 'RATE_DISPUTE', label: 'Rate dispute' },
  { id: 'WORK_QUALITY', label: 'Work quality' },
  { id: 'DOWNTIME', label: 'Downtime / idle' },
  { id: 'OTHER', label: 'Other' },
];

function formatMoney(value) {
  return Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
}

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function formatDateTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return formatDate(value);
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function isPastDue(value) {
  if (!value) return false;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return false;
  return date.getTime() < Date.now();
}

function listPathForRole(role) {
  if (role === 'owner') return '/api/owner/contract-ra-bills';
  if (role === 'admin') return '/api/admin/contract-ra-bills';
  return '/api/client/contract-ra-bills';
}

/**
 * Contract RA-bill panel: review, dispute, wallet pay (same pattern as booking confirm).
 * Generation is admin-triggered only in this checkpoint (no cycle-end cron).
 */
export default function ContractRaBillPanel({
  booking,
  role = 'client',
  walletUser = null,
  t = (key) => key,
}) {
  const bookingId = booking?.id;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [cycleType, setCycleType] = useState('full');
  const [cycleDays, setCycleDays] = useState('');
  const [forceMajeureReason, setForceMajeureReason] = useState('');
  const standbyPercent = Number(booking?.standby_percent ?? 50);

  const [disputeBillId, setDisputeBillId] = useState(null);
  const [reasonCode, setReasonCode] = useState('');
  const [description, setDescription] = useState('');
  const [disputedAmount, setDisputedAmount] = useState('');
  const [evidenceUrl, setEvidenceUrl] = useState('');

  const [payBill, setPayBill] = useState(null);
  const [walletBalance, setWalletBalance] = useState(null);
  const [showRecharge, setShowRecharge] = useState(false);
  const [rechargeAmt, setRechargeAmt] = useState(500);
  const [paying, setPaying] = useState(false);

  const load = useCallback(async () => {
    if (!bookingId || booking?.booking_type !== 'contract') return;
    setLoading(true);
    setError('');
    try {
      const data = await secureFetch(`${listPathForRole(role)}?bookingId=${encodeURIComponent(bookingId)}`);
      if (data?.success === false) {
        throw new Error(data.message || 'Could not load RA-bills');
      }
      setItems(Array.isArray(data?.items) ? data.items : []);
    } catch (err) {
      setError(err?.message || 'Could not load RA-bills');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [bookingId, booking?.booking_type, role]);

  useEffect(() => {
    load();
  }, [load]);

  if (!booking || booking.booking_type !== 'contract') return null;

  const refreshWallet = async () => {
    if (!walletUser?.id) return null;
    const bal = await getWalletBalance(walletUser.id);
    setWalletBalance(bal);
    return bal;
  };

  const handleGenerate = async () => {
    if (role !== 'admin' || generating) return;
    if (cycleType === 'partial' && !(Number(cycleDays) > 0)) {
      setError('Enter cycle days for partial billing');
      return;
    }
    setGenerating(true);
    setError('');
    try {
      const result = await secureFetch(`/api/admin/ra-bills/${encodeURIComponent(bookingId)}/generate-cycle`, {
        method: 'POST',
        body: JSON.stringify({
          cycleType,
          cycleDays: cycleType === 'partial' ? Number(cycleDays) : null,
          forceMajeureReason: cycleType === 'force_majeure' ? (forceMajeureReason || null) : null,
        }),
      });
      if (result?.success === false) {
        throw new Error(result.message || 'Could not generate RA-bill');
      }
      await load();
    } catch (err) {
      setError(err?.message || 'Could not generate RA-bill');
    } finally {
      setGenerating(false);
    }
  };

  const handleAccept = async (billId) => {
    if (role !== 'client' || busyId) return;
    setBusyId(billId);
    setError('');
    try {
      const result = await secureFetch(`/api/client/contract-ra-bills/${billId}/accept`, {
        method: 'POST',
        body: '{}',
      });
      if (result?.success === false) {
        throw new Error(result.message || 'Could not accept RA-bill');
      }
      await load();
    } catch (err) {
      setError(err?.message || 'Could not accept RA-bill');
    } finally {
      setBusyId(null);
    }
  };

  const handleDispute = async () => {
    if (role !== 'client' || !disputeBillId || busyId) return;
    setBusyId(disputeBillId);
    setError('');
    try {
      const result = await secureFetch(`/api/client/contract-ra-bills/${disputeBillId}/disputes`, {
        method: 'POST',
        body: JSON.stringify({
          reasonCode,
          description,
          disputedAmount: Number(disputedAmount),
          evidenceUrl,
        }),
      });
      if (result?.success === false) {
        throw new Error(result.message || 'Could not file dispute');
      }
      setDisputeBillId(null);
      setReasonCode('');
      setDescription('');
      setDisputedAmount('');
      setEvidenceUrl('');
      await load();
    } catch (err) {
      setError(err?.message || 'Could not file dispute');
    } finally {
      setBusyId(null);
    }
  };

  const openPay = async (bill) => {
    setError('');
    setPayBill(bill);
    const bal = await refreshWallet();
    const due = Number(bill.amount_due_from_client || 0);
    if (bal != null && bal < due) {
      setRechargeAmt(Math.max(500, Math.ceil(due - bal)));
      setShowRecharge(true);
    }
  };

  const handleConfirmPay = async () => {
    if (!payBill || paying) return;
    const due = Number(payBill.amount_due_from_client || 0);
    const bal = walletBalance ?? (await refreshWallet()) ?? 0;
    if (due > 0 && bal < due) {
      setRechargeAmt(Math.max(500, Math.ceil(due - bal)));
      setShowRecharge(true);
      return;
    }

    setPaying(true);
    setError('');
    try {
      const result = await secureFetch(`/api/client/contract-ra-bills/${payBill.id}/pay`, {
        method: 'POST',
        body: '{}',
      });
      if (result?.success === false) {
        if (result.code === 'INSUFFICIENT_BALANCE') {
          setRechargeAmt(Math.max(500, Math.ceil(due - (walletBalance || 0))));
          setShowRecharge(true);
          throw new Error(result.message || 'Insufficient wallet balance — recharge to continue');
        }
        throw new Error(result.message || 'Payment failed');
      }
      if (result.newBalance != null) setWalletBalance(Number(result.newBalance));
      setPayBill(null);
      await load();
    } catch (err) {
      setError(err?.message || 'Payment failed');
    } finally {
      setPaying(false);
    }
  };

  const reviewOpen = (status) => ['generated', 'under_review', 'partially_disputed'].includes(status);
  const canPay = (bill) => {
    if (bill.payment_reference || bill.status === 'settled') return false;
    return ['accepted', 'deemed_accepted', 'partially_disputed'].includes(bill.status);
  };

  const machineTerms = booking?.machines || {};
  const earlyDiscountPct = Number(machineTerms.early_payment_discount_pct || 0);
  const lateFeePct = Number(machineTerms.late_payment_fee_pct || 0);

  return (
    <div className="mt-3 rounded-lg border border-sky-400/20 bg-sky-400/5 p-3">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <p className="m-0 text-[11px] font-bold uppercase tracking-wide text-sky-300">
          Running Account bills
        </p>
        {role === 'admin' && (
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:items-end">
            <select
              value={cycleType}
              onChange={(e) => setCycleType(e.target.value)}
              className="rounded border border-[#C9A84C]/40 bg-[#030810] px-2 py-1 text-[11px] text-[#C9A84C]"
            >
              <option value="full">Full</option>
              <option value="standby">Standby</option>
              <option value="force_majeure">Force Majeure</option>
              <option value="partial">Partial</option>
            </select>
            {cycleType === 'standby' && (
              <p className="m-0 text-[10px] text-[#C9A84C]/80">
                Standby % (from contract): {standbyPercent}%
              </p>
            )}
            {cycleType === 'partial' && (
              <input
                type="number"
                min="1"
                value={cycleDays}
                onChange={(e) => setCycleDays(e.target.value)}
                placeholder="Cycle days"
                className="rounded border border-[#C9A84C]/40 bg-[#030810] px-2 py-1 text-[11px] text-slate-100"
              />
            )}
            {cycleType === 'force_majeure' && (
              <input
                type="text"
                value={forceMajeureReason}
                onChange={(e) => setForceMajeureReason(e.target.value)}
                placeholder="Force majeure reason"
                className="rounded border border-[#C9A84C]/40 bg-[#030810] px-2 py-1 text-[11px] text-slate-100"
              />
            )}
            <button
              type="button"
              onClick={handleGenerate}
              disabled={generating}
              className="rounded border border-[#C9A84C]/40 bg-[#C9A84C]/10 px-2 py-1 text-[11px] font-semibold text-[#C9A84C] disabled:opacity-50"
            >
              {generating ? 'Generating…' : 'Generate next cycle RA-bill'}
            </button>
          </div>
        )}
      </div>
      <p className="mb-2 text-[10px] text-slate-500">
        RA-bills are auto-generated at 01:00 AM IST on cycle end date. Hours are evidentiary only — cycle amount is the fixed rate per cycle.
      </p>

      {loading && <p className="m-0 text-[11px] text-slate-400">Loading RA-bills…</p>}
      {error && <p className="mb-2 text-[11px] text-red-400">{error}</p>}
      {!loading && items.length === 0 && (
        <p className="m-0 text-[11px] text-slate-500">No RA-bills generated yet.</p>
      )}

      <div className="flex flex-col gap-2">
        {items.map((bill) => (
          <div key={bill.id} className="rounded border border-white/10 bg-black/20 p-2 text-[11px] text-slate-300">
            <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
              <span className="font-semibold text-sky-200">
                Cycle {bill.cycle_number}
                {bill.cycle_type && bill.cycle_type !== 'full'
                  ? ` · ${String(bill.cycle_type).replace(/_/g, ' ')}`
                  : ''}
                {' · '}
                {formatDate(bill.cycle_start_date)} – {formatDate(bill.cycle_end_date)}
              </span>
              <span className="rounded-full border border-white/20 px-2 py-0.5 text-[10px] uppercase">
                {String(bill.status || '').replace(/_/g, ' ')}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-1 md:grid-cols-4">
              <span>GMH: {formatMoney(bill.guaranteed_hours)} h</span>
              <span>Confirmed: {formatMoney(bill.actual_confirmed_hours)} h</span>
              <span>Auto-unconfirmed: {formatMoney(bill.actual_auto_unconfirmed_hours)} h</span>
              <span>Billed (evidence): {formatMoney(bill.billed_hours)} h</span>
              <span>Base: ₹{formatMoney(bill.base_amount)}</span>
              <span>GST: ₹{formatMoney(bill.gst_amount)}</span>
              <span>Total: ₹{formatMoney(bill.total_amount)}</span>
              <span>Due: ₹{formatMoney(bill.amount_due_from_client)}</span>
            </div>
            {Number(bill.advance_credit_applied || 0) > 0 && (
              <p className="mb-0 mt-1 text-[10px] text-amber-200">
                Cycle 1 advance credit applied: ₹{formatMoney(bill.advance_credit_applied)}
              </p>
            )}
            {(bill.mandate_debit_status === 'pending'
              || bill.mandate_debit_status === 'debited'
              || bill.mandate_debit_status === 'failed') && (
              <div className="mt-1 space-y-0.5">
                {bill.mandate_debit_status === 'pending' && (
                  <p className="mb-0 text-[10px] font-semibold text-amber-200">
                    ⏳ Auto-debit initiated
                  </p>
                )}
                {bill.mandate_debit_status === 'debited' && (
                  <p className="mb-0 text-[10px] font-semibold text-emerald-300">
                    ✅ Auto-debited via e-NACH
                  </p>
                )}
                {bill.mandate_debit_status === 'failed' && (
                  <p className="mb-0 text-[10px] font-semibold text-red-400">
                    ❌ Auto-debit failed — pay manually
                  </p>
                )}
                {(bill.mandate_bank_name
                  || booking?.mandate_bank_name
                  || booking?.contract_quotation?.mandate_bank_name) && (
                  <p className="mb-0 text-[10px] text-slate-500">
                    Bank:
                    {' '}
                    {bill.mandate_bank_name
                      || booking?.mandate_bank_name
                      || booking?.contract_quotation?.mandate_bank_name}
                  </p>
                )}
              </div>
            )}
            {(bill.payment_due_date || bill.early_payment_deadline) && bill.status !== 'settled' && (
              <div className="mt-1 space-y-0.5">
                {bill.payment_due_date && (
                  <p
                    className={`mb-0 text-[10px] font-semibold ${
                      isPastDue(bill.payment_due_date) ? 'text-red-400' : 'text-[#C9A84C]'
                    }`}
                  >
                    Pay by: {formatDateTime(bill.payment_due_date)}
                  </p>
                )}
                {bill.early_payment_deadline
                  && earlyDiscountPct > 0
                  && !isPastDue(bill.early_payment_deadline) && (
                  <p className="mb-0 text-[10px] font-medium text-emerald-300">
                    Pay before {formatDateTime(bill.early_payment_deadline)}
                    {' → '}
                    save {earlyDiscountPct}%
                  </p>
                )}
                {bill.payment_due_date && isPastDue(bill.payment_due_date) && lateFeePct > 0 && (
                  <p className="mb-0 text-[10px] font-medium text-red-400">
                    Late fee {lateFeePct}% applies
                  </p>
                )}
              </div>
            )}
            {bill.review_deadline && reviewOpen(bill.status) && (
              <p className="mb-0 mt-1 text-[10px] text-slate-500">
                Review deadline: {new Date(bill.review_deadline).toLocaleString('en-IN')}
              </p>
            )}

            {role === 'client' && (
              <div className="mt-2 flex flex-wrap gap-2">
                {reviewOpen(bill.status) && (
                  <>
                    <button
                      type="button"
                      disabled={busyId === bill.id}
                      onClick={() => handleAccept(bill.id)}
                      className="rounded border border-emerald-400/40 bg-emerald-400/10 px-2 py-1 text-[11px] font-semibold text-emerald-200 disabled:opacity-50"
                    >
                      Accept bill
                    </button>
                    <button
                      type="button"
                      onClick={() => setDisputeBillId(bill.id)}
                      className="rounded border border-amber-400/40 bg-amber-400/10 px-2 py-1 text-[11px] font-semibold text-amber-200"
                    >
                      File dispute
                    </button>
                    <a
                      href="https://samadhaan.msme.gov.in"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center rounded border border-sky-400/35 bg-sky-400/10 px-2 py-1 text-[11px] font-semibold text-sky-200 hover:bg-sky-400/20"
                    >
                      Resolve via ODR Portal (MSMED Act 2026) →
                    </a>
                  </>
                )}
                {canPay(bill) && (
                  <button
                    type="button"
                    onClick={() => openPay(bill)}
                    className="rounded border border-de-gold/40 bg-de-gold/10 px-2 py-1 text-[11px] font-semibold text-de-gold"
                  >
                    Pay from wallet
                  </button>
                )}
                {bill.status === 'settled' && (
                  <span className="text-[10px] font-semibold text-emerald-300">Settled</span>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {disputeBillId && (
        <div className="mt-3 rounded border border-amber-400/30 bg-black/40 p-3">
          <p className="mb-2 text-[11px] font-semibold text-amber-200">File itemized dispute</p>
          <label className="mb-2 block text-[10px] text-slate-400">
            Reason code
            <select
              value={reasonCode}
              onChange={(e) => setReasonCode(e.target.value)}
              className="mt-1 w-full rounded border border-white/20 bg-slate-950 px-2 py-1.5 text-slate-100"
            >
              <option value="">Select reason</option>
              {REASON_CODES.map((r) => (
                <option key={r.id} value={r.id}>{r.label}</option>
              ))}
            </select>
          </label>
          <label className="mb-2 block text-[10px] text-slate-400">
            Description (min 10 characters)
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="mt-1 w-full rounded border border-white/20 bg-slate-950 px-2 py-1.5 text-slate-100"
            />
          </label>
          <label className="mb-2 block text-[10px] text-slate-400">
            Disputed amount (₹)
            <input
              type="number"
              min="0.01"
              step="0.01"
              value={disputedAmount}
              onChange={(e) => setDisputedAmount(e.target.value)}
              className="mt-1 w-full rounded border border-white/20 bg-slate-950 px-2 py-1.5 text-slate-100"
            />
          </label>
          <label className="mb-2 block text-[10px] text-slate-400">
            Evidence URL (required, http/https)
            <input
              type="url"
              value={evidenceUrl}
              onChange={(e) => setEvidenceUrl(e.target.value)}
              placeholder="https://..."
              className="mt-1 w-full rounded border border-white/20 bg-slate-950 px-2 py-1.5 text-slate-100"
            />
          </label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setDisputeBillId(null)}
              className="rounded border border-white/20 px-2 py-1 text-[11px] text-slate-300"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={busyId === disputeBillId}
              onClick={handleDispute}
              className="rounded border border-amber-400/40 bg-amber-400/10 px-2 py-1 text-[11px] font-semibold text-amber-200 disabled:opacity-50"
            >
              Submit dispute
            </button>
          </div>
          <a
            href="https://samadhaan.msme.gov.in"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 inline-block text-[11px] font-semibold text-sky-300 underline underline-offset-2 hover:text-sky-200"
          >
            Resolve via ODR Portal (MSMED Act 2026) →
          </a>
          <p className="mt-2 text-[10px] leading-relaxed text-slate-400">
            Courts must order minimum 50% upfront payment of arbitral awards (MSMED Amendment 2026)
          </p>
        </div>
      )}

      {payBill && !showRecharge && (
        <div className="mt-3 rounded border border-de-gold/30 bg-black/50 p-3">
          <p className="mb-2 text-[12px] font-bold text-de-gold">Confirm RA-bill payment</p>
          <div className="mb-2 space-y-1 text-[11px] text-slate-300">
            <div className="flex justify-between"><span>Cycle</span><span>{payBill.cycle_number}</span></div>
            <div className="flex justify-between"><span>Amount due</span><span className="font-bold text-de-gold">₹{formatMoney(payBill.amount_due_from_client)}</span></div>
            <div className="flex justify-between"><span>Wallet balance</span><span>₹{formatMoney(walletBalance)}</span></div>
            <div className="flex justify-between">
              <span>Wallet after</span>
              <span className="text-emerald-400">
                ₹{formatMoney((walletBalance ?? 0) - Number(payBill.amount_due_from_client || 0))}
              </span>
            </div>
          </div>
          <p className="mb-2 text-[10px] text-amber-200">
            Amount is deducted from your wallet (same as booking advance). Recharge with Razorpay if balance is low.
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setPayBill(null)}
              disabled={paying}
              className="rounded border border-white/20 px-2 py-1 text-[11px] text-slate-300"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirmPay}
              disabled={paying}
              className="rounded border border-de-gold/40 bg-de-gold/20 px-2 py-1 text-[11px] font-semibold text-de-gold disabled:opacity-50"
            >
              {paying ? 'Processing…' : 'Confirm payment'}
            </button>
          </div>
        </div>
      )}

      {showRecharge && walletUser?.id && (
        <div className="mt-3 rounded border border-de-gold/30 bg-black/50 p-3">
          <p className="mb-2 text-[12px] font-bold text-de-gold">Wallet recharge</p>
          <p className="mb-2 text-[11px] text-slate-400">
            Current balance: ₹{formatMoney(walletBalance)}. Recharge, then confirm RA-bill payment.
          </p>
          <WalletRechargePanel
            t={t}
            userId={walletUser.id}
            userName={walletUser.name || 'Client'}
            userEmail={walletUser.email || 'contact@developmentexpress.in'}
            userPhone={walletUser.phone || ''}
            rechargeAmt={rechargeAmt}
            setRechargeAmt={setRechargeAmt}
            onBalanceUpdated={async (bal) => {
              setWalletBalance(bal);
              setShowRecharge(false);
            }}
            isMobile
          />
          <button
            type="button"
            className="mt-2 rounded border border-white/20 px-2 py-1 text-[11px] text-slate-300"
            onClick={() => setShowRecharge(false)}
          >
            Close
          </button>
        </div>
      )}
    </div>
  );
}
